/**
 * iter198 — Pièces jointes d'un e-mail (compose ou réponse). Extrait de
 * Mailbox.jsx en iter257 pour être réutilisé par les deux formulaires.
 */
import { useEffect, useState } from "react";
import { Paperclip, X, FolderSearch, Loader2, FileText, Receipt, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const ICONS = { facture: Receipt, devis: FileText, or_devis: FileText, or_attachment: Wrench };

export default function AttachmentsInput({ attachments, setAttachments, testIdPrefix = "att" }) {
  const MAX_MB = 15;
  const totalMb = attachments.reduce((s, a) => s + (a.size_bytes || 0), 0) / 1_000_000;
  const [busy, setBusy] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [crmQ, setCrmQ] = useState("");
  const [crmItems, setCrmItems] = useState([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [picking, setPicking] = useState(null);

  // iter259 — parcourir les documents du CRM (factures, devis, pièces jointes d'OR)
  useEffect(() => {
    if (!crmOpen) return;
    const timer = setTimeout(async () => {
      setCrmLoading(true);
      try {
        const { data } = await api.get("/mail/crm-documents", { params: { q: crmQ || undefined } });
        setCrmItems(data?.items || []);
      } catch { setCrmItems([]); } finally { setCrmLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [crmQ, crmOpen]);

  const pickCrmDoc = async (item) => {
    setPicking(item.id);
    try {
      const { data } = await api.post("/mail/crm-attachment", { type: item.type, id: item.id });
      setAttachments([...attachments, data]);
      toast.success(`${data.filename} joint`);
      setCrmOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Document indisponible");
    } finally { setPicking(null); }
  };

  const addFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    const next = [...attachments];
    for (const file of files) {
      try {
        const buf = await file.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let bin = "";
        for (const b of bytes) bin += String.fromCharCode(b);
        next.push({
          filename: file.name,
          mime: file.type || "application/octet-stream",
          content_b64: btoa(bin),
          size_bytes: file.size,
        });
      } catch (e) {
        console.error("attach fail", e);
      }
    }
    const newTotalMb = next.reduce((s, a) => s + (a.size_bytes || 0), 0) / 1_000_000;
    if (newTotalMb > MAX_MB) {
      toast.error(`Pièces jointes trop volumineuses (${newTotalMb.toFixed(1)} Mo). Max ${MAX_MB} Mo.`);
      setBusy(false);
      return;
    }
    setAttachments(next);
    setBusy(false);
  };

  return (
    <div className="space-y-1" data-testid={`${testIdPrefix}-attachments`}>
      <div className="flex items-center gap-2">
        <label
          className="cursor-pointer inline-flex items-center gap-1 text-xs text-[#3d8a5c] hover:text-[#2d5f3f]"
          data-testid={`${testIdPrefix}-attach-label`}
        >
          <Paperclip className="w-3.5 h-3.5" />
          <span>{busy ? "Chargement…" : "Joindre un fichier"}</span>
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => addFiles(Array.from(e.target.files || []))}
            data-testid={`${testIdPrefix}-attach-input`}
          />
        </label>
        <button
          type="button"
          onClick={() => { setCrmOpen(true); setCrmQ(""); }}
          className="inline-flex items-center gap-1 text-xs text-[#3d8a5c] hover:text-[#2d5f3f]"
          data-testid={`${testIdPrefix}-crm-browse`}
        >
          <FolderSearch className="w-3.5 h-3.5" /> Depuis le CRM
        </button>
        {attachments.length > 0 && (
          <span className="text-[11px] text-gray-500">
            {attachments.length} fichier(s) · {totalMb.toFixed(1)} Mo / {MAX_MB} Mo
          </span>
        )}
      </div>
      <Dialog open={crmOpen} onOpenChange={setCrmOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Joindre un document du CRM</DialogTitle>
          </DialogHeader>
          <Input
            value={crmQ}
            onChange={(e) => setCrmQ(e.target.value)}
            placeholder="N° de facture, de devis, d'OR, plaque, client…"
            autoFocus
            data-testid={`${testIdPrefix}-crm-search`}
          />
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100 -mx-1">
            {crmLoading && <div className="p-4 text-center text-gray-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>}
            {!crmLoading && crmItems.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-400">Aucun document trouvé</div>
            )}
            {!crmLoading && crmItems.map((it) => {
              const Icon = ICONS[it.type] || FileText;
              return (
                <button
                  key={`${it.type}-${it.id}`}
                  type="button"
                  onClick={() => pickCrmDoc(it)}
                  disabled={!!picking}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2.5 disabled:opacity-50"
                  data-testid={`${testIdPrefix}-crm-item-${it.id}`}
                >
                  <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-gray-900 truncate">{it.titre}</span>
                    <span className="block text-[11px] text-gray-500 truncate">{it.sous_titre}</span>
                  </span>
                  {picking === it.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                </button>
              );
            })}
          </div>
          <div className="pt-2 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setCrmOpen(false)}>Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {attachments.length > 0 && (
        <ul className="space-y-1 text-xs">
          {attachments.map((a, i) => (
            <li
              key={`${a.filename}-${i}`}
              className="flex items-center justify-between bg-gray-50 rounded px-2 py-1"
              data-testid={`${testIdPrefix}-att-${i}`}
            >
              <span className="truncate">
                {a.filename} <span className="text-gray-400">({(a.size_bytes / 1000).toFixed(0)} Ko)</span>
              </span>
              <button
                type="button"
                onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}
                className="text-red-600 hover:text-red-800"
                data-testid={`${testIdPrefix}-att-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
